import React from 'react';
import { type FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { AllowPolicy } from '@wix/ambassador-social-groups-v2-group/types';

import { Box, IBoxProps } from 'wui/Box';

import { selectIsAppAvailable, selectIsSiteAdmin } from 'store/selectors';
import { GroupAppKey } from 'store/groups/types';

import { type MemberPermissionsFormValues } from './types';
import { AllowPolicyField } from './AllowPolicyField';
import {
  getDisabledPolicies,
  getInviteDisabledPolicies,
  isMoreRestrictive,
} from './permissions';

interface IProps {
  form: FormikProps<MemberPermissionsFormValues>;
  groupId: string;
}

export function MemberPermissionsForm(props: IProps & IBoxProps) {
  const { form, groupId, ...rest } = props;

  const { t } = useTranslation();
  const isSiteAdmin = useSelector(selectIsSiteAdmin);
  const isEventsAppAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.EVENTS_APP,
    }),
  );
  const disabledPolicies = getDisabledPolicies(form.values.allowedToInviteMembers);
  const inviteDisabledPolicies = getInviteDisabledPolicies(
    form.values.allowedToInviteMembers,
    isSiteAdmin,
  );
  const dependentHelperText = disabledPolicies.length
    ? t('groups-web.group.settings.permissions-form.disabled.invite-ceiling')
    : undefined;

  return (
    <Box gap="SP8" direction="vertical" {...rest}>
      <AllowPolicyField
        {...form.getFieldProps('allowedToCreatePosts')}
        onChange={(value) => form.setFieldValue('allowedToCreatePosts', value)}
        label={t(
          'groups-web.group.settings.permissions-form.create-posts.label',
        )}
      />

      <AllowPolicyField
        {...form.getFieldProps('allowedToInviteMembers')}
        disabledValues={inviteDisabledPolicies}
        onChange={handleUpdatedInviteMembersChange}
        label={t(
          'groups-web.group.settings.permissions-form.invite-members.label',
        )}
      />

      <AllowPolicyField
        {...form.getFieldProps('allowedToAddMembers')}
        disabledValues={disabledPolicies}
        helperText={dependentHelperText}
        onChange={(value) => form.setFieldValue('allowedToAddMembers', value)}
        label={t(
          'groups-web.group.settings.permissions-form.add-members.label',
        )}
      />

      {isEventsAppAvailable && (
        <AllowPolicyField
          {...form.getFieldProps('allowedToCreateEvents')}
          disabledValues={disabledPolicies}
          helperText={dependentHelperText}
          onChange={(value) =>
            form.setFieldValue('allowedToCreateEvents', value)
          }
          label={t(
            'groups-web.group.settings.permissions-form.create-events.title',
          )}
        />
      )}
    </Box>
  );

  function handleUpdatedInviteMembersChange(value: AllowPolicy) {
    form.setFieldValue('allowedToInviteMembers', value);

    if (isMoreRestrictive(value, form.values.allowedToAddMembers)) {
      form.setFieldValue('allowedToAddMembers', value);
    }

    if (
      isEventsAppAvailable &&
      isMoreRestrictive(value, form.values.allowedToCreateEvents)
    ) {
      form.setFieldValue('allowedToCreateEvents', value);
    }
  }
}

MemberPermissionsForm.displayName = 'MemberPermissionsForm';
